'use client'

import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { subscribeNotifications, type HiveNotificationPayload } from '@/lib/socket-client'

/**
 * Mount once at the app root. Listens to ``notification:new`` from the
 * backend (workflow executor + service health watchdog) and renders a
 * Sonner toast for each one. De-duplicates by ``incident_id`` so the
 * watchdog's "still down" pulses don't pile up.
 */
export function NotificationListener() {
  const seen = useRef<Set<string>>(new Set())

  useEffect(() => {
    const unsubscribe = subscribeNotifications((p: HiveNotificationPayload) => {
      const dedupeKey = p.incident_id ? `${p.incident_id}:${p.event}` : null
      if (dedupeKey && seen.current.has(dedupeKey)) return
      if (dedupeKey) seen.current.add(dedupeKey)

      const title = p.title || p.workflowName || p.service_label || 'Notification'
      const description = p.message || ''

      switch (p.severity) {
        case 'critical':
        case 'error':
          toast.error(title, { description, duration: 8000 })
          break
        case 'warn':
          toast.warning(title, { description, duration: 6000 })
          break
        case 'info':
        default:
          toast(title, { description })
          break
      }
    })
    return unsubscribe
  }, [])

  return null
}
